import type { GetMeUserResponse, UserRoleEnum } from "@nlc/shared-types";
import type { Key } from "react-stately";

import { CUSTOMER_ROLES, USER_ROLES } from "@nlc/shared-types";
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { StarIcon, SunsetIcon, User2Icon } from "lucide-react";
import { useCallback } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { Badge } from "@/components/badge";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { redirectRoute } from "@/features/projects/project/project-tab-routes";
import { FeaturePageLayout, WhitelabelBadge } from "@/features/shared";
import { ProjectInfoPopup } from "@/features/shared/components/sticky-widget/account-information";
import { useProjectInformation } from "@/features/shared/components/sticky-widget/account-information/hooks/useGetProjectDetails";
import { usePageTitle } from "@/hooks/usePageTitle";

export const Route = createFileRoute("/_auth/_app-shell/projects/$projectId")({
  beforeLoad: async ({ context }) => {
    const userData = context.queryClient.getQueryData<GetMeUserResponse>([
      "user",
    ]);

    // Block customers from accessing project details
    const isCustomer = CUSTOMER_ROLES.includes(
      userData?.user?.roleId as UserRoleEnum,
    );

    if (isCustomer) {
      throw redirect({ to: "/requests" as string });
    }
  },
  component: RouteComponent,
});

const TAB_LABELS: Record<string, string> = {
  brands: "Brands",
  edit: "Details",
  "internal-notes": "Notes",
  requests: "Requests",
  users: "Users",
  view: "Requests",
};

function RouteComponent() {
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { projectId } = useParams({
    from: "/_auth/_app-shell/projects/$projectId",
  });

  const { data: projectData } = useProjectInformation(Number(projectId));
  const { hasRole } = useRoleAccess();
  const isDesigner = hasRole(["designer"]);

  const allTabs = [
    { className: "py-0", id: "view-all", label: "View all" },
    { className: "py-0", id: "view", label: "View" },
    { className: "py-0", id: "edit", label: "Details" },
    { className: "py-0", id: "requests", label: "Requests" },
    { className: "py-0", id: "brands", label: "Brands" },
    { className: "py-0", id: "users", label: "Users" },
    { className: "py-0", id: "internal-notes", label: "Internal Notes" },
  ];

  const tabs = isDesigner
    ? allTabs.filter((tab) => tab.id !== "edit" && tab.id !== "users")
    : allTabs;

  const getCurrentTab = (): string => {
    if (matchRoute({ to: "/projects/$projectId" })) return "view";
    if (matchRoute({ to: "/projects/$projectId/edit" })) return "edit";
    if (matchRoute({ to: "/project-associated-requests/$projectId" }))
      return "requests";
    if (matchRoute({ to: "/projects/$projectId/brands" })) return "brands";
    if (matchRoute({ to: "/projects/$projectId/users" })) return "users";
    if (matchRoute({ to: "/projects/$projectId/internal-notes" }))
      return "internal-notes";

    return "view";
  };

  const currentTab = getCurrentTab();

  const projectSection = TAB_LABELS[currentTab] ?? "Requests";

  usePageTitle(
    projectData?.projectName
      ? `${projectData.projectName} - ${projectSection}`
      : "",
  );

  const handleTabChangeCallback = useCallback(
    (key: Key) => {
      handleTabChange(key);
    },
    [projectId],
  );

  const handleTabChange = (key: Key) => {
    const tabKey = key.toString();

    navigate({
      params: { projectId },
      to: redirectRoute(key, tabKey),
    });
  };

  let createButton;

  if (currentTab === "brands") {
    createButton = {
      href: `/brands/create?project-id=${projectId}`,
      label: "New brand",
      requiredRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TEAM_LEADER,
        USER_ROLES.CUSTOMER_SUCCESS,
      ] as UserRole[],
    };
  }
  if (currentTab === "requests") {
    createButton = {
      href: `/requests/pick-type?project-id=${projectId}`,
      label: "New request",
      requiredRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.TEAM_LEADER,
        USER_ROLES.CUSTOMER_SUCCESS,
      ] as UserRole[],
    };
  }

  if (currentTab === "users") {
    createButton = {
      href: `/users/create?project-id=${projectId}`,
      label: "New user",
      requiredRoles: [
        USER_ROLES.SUPER_ADMIN,
        USER_ROLES.CUSTOMER_SUCCESS,
      ] as UserRole[],
    };
  }

  const hasAdornmentContent =
    projectData?.new ||
    projectData?.sunset ||
    projectData?.concierge ||
    projectData?.isWhitelabel;

  const endAdornment = hasAdornmentContent ? (
    <div className="flex gap-2 justify-self-start">
      {projectData?.new && (
        <Badge className="mr-1" isRounded variant="blue">
          <StarIcon className="mr-1" size={14} />
          New
        </Badge>
      )}
      {projectData?.sunset && (
        <Badge className="mr-1" isRounded variant="orange">
          <SunsetIcon className="mr-1" size={14} />
          Sunset
        </Badge>
      )}
      {projectData?.concierge && (
        <Badge className="mr-1" isRounded variant="success">
          <User2Icon className="mr-1" size={14} />
          Concierge
        </Badge>
      )}
      <WhitelabelBadge className="mr-1" role={projectData?.whitelabelRole} />
    </div>
  ) : undefined;

  return (
    <div className="p-xl">
      <FeaturePageLayout
        createButton={createButton}
        headerContent={endAdornment}
        tabs={{
          className: "bg-bg-primary",
          onSelectionChange: handleTabChangeCallback,
          selectedKey: currentTab,
          tabs,
        }}
        title={projectData?.projectName || "Account"}
      >
        <ProjectInfoPopup projectId={Number(projectId)} />
        <Outlet />
      </FeaturePageLayout>
    </div>
  );
}
